/**
 * Resolution de conflits de synchro multi-appareils (`docs/ARCHITECTURE.md`
 * §4 : « revisions + resolution de conflits »).
 *
 * Opere UNIQUEMENT sur des metadonnees claires (`id`, `revision`,
 * `updatedAt`) : cette logique tourne aussi bien cote client que sur des
 * enregistrements encore chiffres (`EncryptedVaultItem`/`EncryptedVaultFolder`),
 * sans jamais avoir besoin de dechiffrer quoi que ce soit. Une suppression
 * (tombstone) est un enregistrement comme un autre : elle gagne ou perd selon
 * la meme regle, pas de cas particulier « la suppression gagne toujours ».
 */
import { InvalidVaultModelInputError } from './errors.js';

export interface ConflictRecord {
  readonly id: string;
  readonly revision: number;
  readonly updatedAt: number;
}

/**
 * `true` si `candidate` doit remplacer `incumbent` : revision strictement
 * plus recente, puis horodatage strictement plus recent. `candidate` et
 * `incumbent` designent toujours deux versions du MEME enregistrement (meme
 * `id` — voir `resolveConflict`), donc departager par `id` serait sans effet.
 * En cas d'egalite parfaite (revision ET updatedAt identiques, ce qui ne
 * traduit aucune divergence reelle), `candidate` ne l'emporte PAS : l'appelant
 * conserve `incumbent`, de facon stable et reproductible.
 */
export function isNewer(candidate: ConflictRecord, incumbent: ConflictRecord): boolean {
  if (candidate.revision !== incumbent.revision) return candidate.revision > incumbent.revision;
  return candidate.updatedAt > incumbent.updatedAt;
}

/**
 * Choisit le vainqueur entre deux versions du MEME enregistrement.
 * Sur egalite parfaite (revision et updatedAt identiques), `local` gagne :
 * un appel `resolveConflict(a, b)` est deterministe, mais n'est PAS
 * symetrique en `a`/`b` dans ce cas limite (il n'y a par definition aucun
 * moyen de departager deux versions strictement identiques en metadonnees).
 */
export function resolveConflict<T extends ConflictRecord>(local: T, remote: T): T {
  if (local === null || remote === null || typeof local !== 'object' || typeof remote !== 'object') {
    throw new InvalidVaultModelInputError('local/remote: enregistrements attendus');
  }
  if (local.id !== remote.id) {
    throw new InvalidVaultModelInputError('resolveConflict: enregistrements d id different');
  }
  return isNewer(remote, local) ? remote : local;
}
